import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from './useAuth'

type TokenClaims = {
  email?: string
  emails?: string[]
  family_name?: string
  given_name?: string
  name?: string
  preferred_username?: string
}

async function getGraphPhoto(accessToken: string): Promise<Blob | null> {
  const response = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (response.status === 404) {
    return null
  }

  if (!response.ok) {
    throw new Error('Unable to fetch Microsoft Graph profile photo')
  }

  return response.blob()
}

export function useMsalProfile() {
  const { instance, account, isAuthenticated } = useAuth()
  const claims = (account?.idTokenClaims ?? {}) as TokenClaims
  const firstName = claims.given_name ?? ''
  const lastName = claims.family_name ?? ''
  const combinedName = `${firstName} ${lastName}`.trim()
  const tokenName = claims.name ?? combinedName
  const fullName = tokenName || account?.name || 'Guest User'
  const email =
    claims.email ?? claims.preferred_username ?? claims.emails?.[0] ?? account?.username ?? ''
  const initials =
    firstName && lastName
      ? `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
      : fullName
          .split(' ')
          .filter(Boolean)
          .slice(0, 2)
          .map((part) => part.charAt(0))
          .join('')
          .toUpperCase() || 'GU'

  const photoQuery = useQuery({
    queryKey: ['msal-profile-photo', account?.homeAccountId],
    queryFn: async () => {
      if (!account) {
        return null
      }

      try {
        const result = await instance.acquireTokenSilent({
          account,
          scopes: ['User.Read'],
        })

        return getGraphPhoto(result.accessToken)
      } catch {
        return null
      }
    },
    enabled: isAuthenticated && !!account,
    retry: false,
    staleTime: 1000 * 60 * 30,
  })

  const [photoUrl, setPhotoUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!photoQuery.data) {
      setPhotoUrl(null)
      return
    }

    const nextUrl = URL.createObjectURL(photoQuery.data)
    setPhotoUrl(nextUrl)

    return () => {
      URL.revokeObjectURL(nextUrl)
    }
  }, [photoQuery.data])

  return {
    email,
    firstName: firstName || 'Guest',
    fullName,
    initials,
    lastName: lastName || 'User',
    photoUrl,
  }
}
